/**
 * lazyWithReload (#3962) - React.lazy for route pages that survives a deploy.
 *
 * Why this exists: App.jsx code-splits every non-entry page into its own
 * content-hashed chunk, so EVERY deploy renames every lazy chunk. A tab that
 * is still running the previous build's entry file asks for
 * `/assets/ChatPage-OLDHASH.js`, which no longer exists. The backend answers
 * that with a plain 404 (the SPA fallback, middleware/spaFallback.js, returns 404
 * for any missing /assets/* file since #4231; before that it answered
 * index.html with HTTP 200, which surfaced as a MIME-type error). Either way
 * the browser reports "Failed to fetch dynamically imported module" and the
 * import() promise rejects, which is all this helper relies on. Unhandled, that
 * reaches Sentry.ErrorBoundary and the learner sees the blank "Something went
 * wrong" fallback on a perfectly healthy deploy.
 *
 * The fix is one full reload, which fetches the new index.html and with it the
 * new chunk names. Guarded so a genuinely broken deploy cannot loop:
 *
 *   - the guard is a per-chunk sessionStorage flag, set BEFORE the reload;
 *   - it is cleared again when that chunk loads successfully, so a later
 *     deploy in the same long session can recover the same way;
 *   - if sessionStorage is unavailable (blocked cookies, private mode) the
 *     guard cannot be written, so we do NOT reload (we could not stop a loop)
 *     and rethrow to the error boundary instead;
 *   - a module-level flag stops two chunks failing together from reloading
 *     twice within one page load.
 *
 * Deliberately NOT here: a global `vite:preloadError` listener. Vite fires that
 * event for EVERY dynamic import (the ChatPage prefetch on /discover, the PDF
 * and screenshot exporters, ...), so a listener would reload the page, and
 * discard an open dialog, for a failure that only a speculative or optional
 * import saw. It is also redundant: when the event is not cancelled Vite
 * rethrows the same error, the page's import() rejects, and loadWithReload
 * below handles it for the routes that need the reload.
 *
 * Only ROUTE pages use lazyWithReload. An optional in-page surface (the Help
 * panel, PDF export, screenshots, the authoring demo dialog) must not: a reload
 * there would interrupt a chat or voice session. Those use lib/importOptional.js,
 * which turns the same failure into a Reload toast the learner chooses to press.
 *
 * Residual (accepted): a learner who is genuinely offline gets the same
 * ErrorFallback as any other render error. The reload happens at most once per
 * chunk per session.
 */
import { lazy } from 'react';
import { safeSessionStorage } from '../utils/safeStorage';

const KEY_PREFIX = 'chunk-reload:';
// How long a triggered reload may take before we stop waiting for it.
const RELOAD_GRACE_MS = 8000;

// True from the moment this page load has asked for a reload. Within one page
// load a second failing chunk must not reload again.
let reloadPending = false;

/** Test seam: reset the per-page-load flag (a real reload does this for free). */
export function _resetReloadStateForTests() {
  reloadPending = false;
}

function defaultReload() {
  window.location.reload();
}

/**
 * Ask for the one guarded reload for `key`.
 * Returns true when a reload was triggered now or is already pending (the
 * caller should then wait instead of throwing), false when it must not reload
 * (already reloaded for this chunk this session, or storage unavailable).
 */
export function requestChunkReload(key, deps = {}) {
  const storage = deps.storage || safeSessionStorage;
  const reload = deps.reload || defaultReload;
  if (reloadPending) return true;
  try {
    const flagKey = KEY_PREFIX + key;
    if (storage.getItem(flagKey)) return false; // already reloaded for this chunk
    // safeSessionStorage.setItem returns false (never throws) when storage is
    // blocked or full: then we cannot remember that we reloaded, so we must not
    // reload at all (a broken deploy would loop forever).
    if (storage.setItem(flagKey, '1') === false) return false;
  } catch {
    return false;
  }
  reloadPending = true;
  reload();
  return true;
}

function clearReloadFlag(key, deps = {}) {
  const storage = deps.storage || safeSessionStorage;
  try {
    storage.removeItem(KEY_PREFIX + key);
  } catch {
    // nothing to clear when storage is unavailable
  }
}

/**
 * Run `importer`; on rejection reload once (see file header) or rethrow.
 * While the reload is in flight the returned promise stays pending, so the
 * Suspense spinner stays up instead of flashing the error boundary; if the
 * reload never happens it rejects with the original error after a grace period.
 */
export function loadWithReload(importer, key, deps = {}) {
  return importer().then(
    (mod) => {
      clearReloadFlag(key, deps);
      return mod;
    },
    (err) => {
      if (requestChunkReload(key, deps)) {
        // Reload under way: stay pending so the Suspense spinner holds. If the
        // page is still here after the grace period the reload was blocked or
        // cancelled; surface the original error (ErrorFallback has a Reload
        // button) instead of a spinner that never ends.
        return new Promise((_resolve, reject) => {
          setTimeout(() => reject(err), RELOAD_GRACE_MS);
        });
      }
      throw err;
    },
  );
}

/**
 * React.lazy over loadWithReload. `key` names the chunk for the reload guard.
 *
 * `.prefetch()` starts the import early (the ChatPage warm-up on /discover).
 * The route then awaits THAT SAME promise, never a second import(): in a build,
 * Vite's preload helper marks each CSS dependency as seen before its <link>
 * loads and does not forget a failure, so a retried import() in this document
 * resolves the JS without its stylesheet (an unstyled page, no error). Reusing
 * the promise means a failed prefetch rejects the route into the guarded
 * reload above (a fresh document has an empty seen map), and a prefetch still
 * in flight is waited for, CSS included. A failed prefetch is otherwise silent.
 */
export function lazyWithReload(importer, key) {
  let started = null;
  const importOnce = () => {
    if (!started) started = importer();
    return started;
  };
  const Component = lazy(() => loadWithReload(importOnce, key));
  Component.prefetch = () => {
    importOnce().catch(() => {});
  };
  return Component;
}
